import { pillarHref, pillars } from '../data/pillars'

type PageMeta = { title: string; description: string }

const defaultMeta: PageMeta = {
  title: 'SyNeural — Building intelligence in the world',
  description:
    'SyNeural builds systems that help people develop sound judgment, researchers connect ideas, and organizations turn knowledge into action.',
}

const pillarMeta: Record<string, PageMeta> = Object.fromEntries(
  pillars.map((pillar) => [
    pillarHref(pillar.id),
    { title: `${pillar.name} — NCJI pillar ${pillar.order} | SyNeural`, description: pillar.description },
  ]),
)

const pageMeta: Record<string, PageMeta> = {
  ...pillarMeta,
  '/': {
    title: 'SyNeural — Connected knowledge. Stronger judgment. Greater human capability.',
    description:
      'SyNeural connects governed knowledge, structured capability development and practical action through the Semantic Knowledge Platform, Human Capability Framework and Noetivra workspace.',
  },
  '/human-capability': {
    title: 'Human Capability Framework (HCF) — SyNeural',
    description: 'Developing reasoning, judgment and readiness for responsibility through structured assessment, human review and development.',
  },
  '/ncji': {
    title: 'NCJI — Five pillars of sound judgment | SyNeural',
    description: 'The Neurocognitive Civic Judgment Index: the five-pillar judgment assessment and development model within the Human Capability Framework.',
  },
  '/noetivra': {
    title: 'Noetivra — One workspace for capability, support and institutional action | SyNeural',
    description: 'Noetivra brings the SyNeural architecture into an interactive workspace for citizens and administrators. Try the interactive prototype.',
  },
  '/government': {
    title: 'National Capability and Development System — SyNeural',
    description: 'SyNeural’s proposed National Capability and Development System connects knowledge, assessments, development plans, public services and program evaluation.',
  },
  '/research': {
    title: 'Research — SyNeural',
    description:
      'The research questions, papers, assessment methods, and evidence behind SyNeural’s systems and projects.',
  },
  '/projects': {
    title: 'Projects — SyNeural',
    description:
      'Named projects built using SyNeural’s connected knowledge and human capability systems, starting with Cognocracy.',
  },
  '/projects/cognocracy': {
    title: 'Cognocracy — A proposed civic framework | SyNeural',
    description:
      'Cognocracy is a concept proposal powered by SyNeural, exploring how structured assessment, capability development, and verified credentials could support institutional trust and readiness for public responsibility.',
  },
}

export function applyPageMeta(path: string) {
  const meta = pageMeta[path] ?? defaultMeta
  document.title = meta.title
  setMeta('name', 'description', meta.description)
  setMeta('property', 'og:title', meta.title)
  setMeta('property', 'og:description', meta.description)
}

function setMeta(attribute: 'name' | 'property', key: string, content: string) {
  let tag = document.head.querySelector<HTMLMetaElement>(`meta[${attribute}="${key}"]`)
  if (!tag) {
    tag = document.createElement('meta')
    tag.setAttribute(attribute, key)
    document.head.appendChild(tag)
  }
  tag.setAttribute('content', content)
}
